import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/lib/supabase';
import { sanitizeInternalPath } from '@/lib/url';

let registered = false;

function sanitizeNext(next: string | null) {
  return sanitizeInternalPath(next);
}

/**
 * Completes the magic-link / OAuth flow when the app is reopened through the
 * `fr.lafete.app://auth/callback` deep link. Because the link is handled by the
 * native bridge (not a real page load), Supabase's `detectSessionInUrl` never
 * runs, so we parse the callback URL ourselves and establish the session.
 */
export function registerAuthDeepLink(onComplete: (next: string) => void) {
  if (!Capacitor.isNativePlatform() || registered) return;
  registered = true;

  void App.addListener('appUrlOpen', async ({ url }) => {
    if (!url || !url.includes('auth/callback')) return;

    try {
      const parsed = new URL(url);
      const hash = new URLSearchParams(parsed.hash.replace(/^#/, ''));
      const next = sanitizeNext(parsed.searchParams.get('next'));

      const errorDescription = parsed.searchParams.get('error_description') ?? hash.get('error_description');
      if (errorDescription) {
        console.error('[auth] deep link error', errorDescription);
        return;
      }

      const code = parsed.searchParams.get('code');
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) throw error;
      } else {
        const accessToken = hash.get('access_token');
        const refreshToken = hash.get('refresh_token');
        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
          if (error) throw error;
        }
      }

      onComplete(next);
    } catch (e) {
      console.error('[auth] deep link handling failed', e);
    }
  });
}
